import { Fragment, ReactNode, useCallback, useMemo, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import styled from 'styled-components';

import { Badge } from '@components/badge';
import { Card } from '@components/card';
import { DesignAvatar } from '@components/design-avatar';
import { IconButton } from '@components/icon-button';
import { IconDateSmall } from '@components/icons/date-small';
import { IconPerson } from '@components/icons/person';
import { IconRemove } from '@components/icons/remove';
import { IconRename } from '@components/icons/rename';
import { IconSidemenuRightOpen } from '@components/icons/sidemenu-right-open';
import { IconStatus } from '@components/icons/status';
import { IconSubIndicator } from '@components/icons/sub-indicator';
import { Menu } from '@components/menu';
import { SquareImage } from '@components/square-image';
import { Horizontal, Stack } from '@components/stack';
import { ColumnIcon, SmallColumnIcon } from '@components/table/column-icon';
import { TableStore } from '@components/table/table-store';
import { AvailableColumn, ColumnEditValue, EditOption, TableView } from '@components/table/types';
import { Text } from '@components/text';
import { showError } from '@components/toaster';
import { useEvent } from '@hooks/use-event';
import { theme } from '@styles/theme';

import {
  AttributeFieldType,
  AttributeTemplate,
  AttributeType,
  TravellerAttributeTemplate,
} from 'api/attributes/requests';
import { useInlineEditCompanyTraveller } from 'api/company-traveller/hooks';
import { loadList } from 'api/lists/requests';
import { handlePossibleNonFieldError, queryClient } from 'api/query-client';
import { useFetchTravellerFields } from 'api/traveller-journeys/hooks';
import { editJourneyTraveller } from 'api/traveller-journeys/requests';
import {
  ActionsDataAction,
  ActionsDataField,
  JourneyType,
  TravellerJourneyListItem,
} from 'api/types';
import { User } from 'api/users/requests';
import { TableUserCell } from 'components/table-user-cell';
import { getActionLabel } from 'ui/actions/action-config';
import { ActionIcon } from 'ui/actions/action-config';
import { ActionLabel } from 'ui/actions/action-config';
import { appendAttributeValueToFormData } from 'utils/attribute-form-data';
import {
  AttributeEditPatch,
  applyAttributeEditToAttributes,
  patchCompanyTravellerListCaches,
  patchTravellerJourneyListCache,
  patchTravellerJourneyListCacheByCompanyTraveller,
  readCachedAttribute,
  reconcileAfterElasticRefresh,
  startFieldEdit,
} from 'utils/inline-edit-cache';

import { IssuesCell } from './cells/issues';
import { NameCell } from './cells/name';
import { PhoneCell } from './cells/phone';
import { ProgressCell } from './cells/progress';
import { StatusCell } from './cells/status';
import { actionStatusLabelMap, statusLabelMap, statusTagMap } from './constants';
import { ExtraStateStore } from './extra-state-store';
import { t } from './locale';
import { ShowCustomNameModal, useSetCustomNameModal } from './set-custom-name-modal';
import { TravellersColumnControlStore } from './travellers-column-control-store';

import { getValueColumn } from '../../../helpers';
import { ShowCompanyTravellerToJourneySidebar } from '../../travellers/company-traveller-to-journey';

// Every type here is handled by EditableCell except paragraph, which has no
// inline editor: setting onEdit on it sends the cell to the legacy free-text
// modal, where a long value has room to be read and written.
export const INLINE_EDITABLE_ATTRIBUTE_TYPES: Array<AttributeFieldType> = [
  'text',
  'paragraph',
  'email',
  'phone_number',
  'single_select',
  'multi_select',
  'date',
  'user',
  'file',
  'file_multi',
  'number',
  'decimal',
];

const MODAL_EDITED_ATTRIBUTE_TYPES: Array<AttributeFieldType> = ['paragraph'];

// The free-text modal closes on save and has nowhere to put a failure, so it
// still needs a toast from the mutation. Every EditableCell editor shows the
// failure itself and would otherwise raise a second, vaguer message.
export const hasOwnErrorSurface = (attributeType: AttributeFieldType): boolean => {
  return !MODAL_EDITED_ATTRIBUTE_TYPES.includes(attributeType);
};

export const createBaseColumns = ({
  onEditContactField,
}: {
  onEditContactField?: (
    row: TravellerJourneyListItem,
    field: 'email' | 'phone',
    value: string
  ) => Promise<void>;
} = {}): Array<AvailableColumn<TravellerJourneyListItem>> => {
  return [
    {
      id: 'name',
      title: t('core.fullName'),
      type: 'text',
      renderHeaderIcon: () => <IconPerson size={16} />,
      Cell: NameCell,
    },
    {
      id: 'email',
      title: t('core.email'),
      type: 'email',
      isClearable: false,
      onEdit: onEditContactField
        ? (row, value) => onEditContactField(row, 'email', value as string)
        : undefined,
    },
    {
      id: 'phone',
      title: t('core.phone'),
      type: 'phone_number',
      Cell: onEditContactField ? undefined : PhoneCell,
      onEdit: onEditContactField
        ? (row, value) => onEditContactField(row, 'phone', value as string)
        : undefined,
    },
    {
      id: 'progress',
      title: 'Progress',
      type: 'progress',
      Cell: ProgressCell,
    },
    {
      id: 'status',
      title: t('status'),
      type: 'single_select',
      renderHeaderIcon: () => <IconStatus size={16} />,
      labelMap: statusLabelMap,
      Cell: StatusCell,
    },
    {
      id: 'dateUpdated',
      title: t('date_modified'),
      type: 'date',
    },
    {
      id: 'dateCreated',
      title: t('date_created'),
      type: 'date',
    },
    {
      id: 'issues',
      title: t('issues'),
      type: 'issues',
      Cell: IssuesCell,
    },
  ];
};

// Someone who was invited but never signed in isn't a person you can hand work
// to yet, and the API blanks their name (along with phone and picture) until
// their first login, so they would show up as unlabelled rows. `status` is no
// help here - it reads "joined" as soon as the invite carries a name.
const mapUsersToEditOptions = (usersData: Array<User>): EditOption[] =>
  usersData
    .filter((user) => Boolean(user.lastLogin))
    .map((user) => ({
      value: user.shortId,
      label: user.name || user.email || user.shortId,
      imageSrc: user.picture ?? null,
    }));

export const resolveAttributeEditOptions = (
  attribute: AttributeType | TravellerAttributeTemplate,
  usersData: Array<User>
): EditOption[] | undefined => {
  if (attribute.attributeType === 'user') {
    return mapUsersToEditOptions(usersData);
  }
  if (attribute.attributeType === 'single_select' || attribute.attributeType === 'multi_select') {
    const list = queryClient.getQueryData<{ options?: Array<{ shortId: string; label: string }> }>([
      'lists',
      attribute.options,
    ]);
    return (list?.options ?? []).map((option) => ({ value: option.shortId, label: option.label }));
  }
  return undefined;
};

export const buildAttributeGetEditOptions = (
  attribute: AttributeType | TravellerAttributeTemplate,
  usersData: Array<User>
): (() => Promise<EditOption[]>) | undefined => {
  if (attribute.attributeType === 'single_select' || attribute.attributeType === 'multi_select') {
    return () =>
      queryClient
        .fetchQuery(['lists', attribute.options], () => loadList(attribute.options ?? ''))
        .then((list) =>
          (list.options ?? []).map((option) => ({ value: option.shortId, label: option.label }))
        );
  }
  if (attribute.attributeType === 'user') {
    return async () => mapUsersToEditOptions(usersData);
  }
  return undefined;
};

export const useAttributeColumns = ({
  attributes,
  usersData = [],
  showEditTravellerSidebar,
  editableJourneyAttributes = true,
  editableCompanyTravellerAttributes = true,
}: {
  attributes: Array<AttributeType | TravellerAttributeTemplate>;
  usersData?: Array<User>;
  showEditTravellerSidebar: ShowCompanyTravellerToJourneySidebar;
  editableJourneyAttributes?: boolean;
  editableCompanyTravellerAttributes?: boolean;
}): Array<AvailableColumn<TravellerJourneyListItem>> => {
  const editCompanyTraveller = useInlineEditCompanyTraveller();

  return useMemo(() => {
    return attributes.flatMap((attribute) => {
      const attributeId = makeAttributeId(
        attribute.shortId,
        Boolean(attribute.isTravellerAttribute)
      );
      const isEditable = attribute.isTravellerAttribute
        ? editableCompanyTravellerAttributes
        : editableJourneyAttributes;

      const getEditOptions = isEditable
        ? buildAttributeGetEditOptions(attribute, usersData)
        : undefined;
      const isFileType =
        attribute.attributeType === 'file' || attribute.attributeType === 'file_multi';

      return [
        {
          id: attributeId,
          title: attribute.title,
          type: attribute.attributeType,
          onMultiFilesClick:
            isFileType && isEditable
              ? undefined
              : (traveller) => {
                  showEditTravellerSidebar({
                    journeyShortId: traveller.journey,
                    journeyTravellerId: traveller.shortId,
                  });
                },
          Cell:
            attribute.attributeType === 'user' && !isEditable
              ? ({ row }) => <TableUserCell row={row} attribute={attribute} users={usersData} />
              : undefined,
          defaultHidden: true,
          getEditOptions,
          onEdit:
            isEditable && INLINE_EDITABLE_ATTRIBUTE_TYPES.includes(attribute.attributeType)
              ? async (row, value: ColumnEditValue) => {
                  const formData = new FormData();
                  const patch: AttributeEditPatch = {
                    attributeShortId: attribute.shortId,
                    attributeTitle: attribute.title,
                    attributeType: attribute.attributeType,
                    value,
                    options: resolveAttributeEditOptions(attribute, usersData),
                  };

                  if (attribute.isTravellerAttribute) {
                    appendAttributeValueToFormData({
                      formData,
                      key: `company_traveller_attribute__${attribute.shortId}`,
                      attributeType: attribute.attributeType,
                      value,
                    });

                    const beforeAttribute = row.companyTravellerAttributes.find(
                      (existing) => existing.attributeShortId === attribute.shortId
                    );
                    const fieldEdit = startFieldEdit(
                      `${row.companyTravellerShortId}:${attribute.shortId}`,
                      beforeAttribute
                    );

                    await editCompanyTraveller.mutateAsync({
                      shortId: row.companyTravellerShortId,
                      data: formData,
                      isErrorShownByCaller: hasOwnErrorSurface(attribute.attributeType),
                      applyOptimistic: () => {
                        patchTravellerJourneyListCacheByCompanyTraveller(
                          queryClient,
                          row.journey,
                          row.companyTravellerShortId,
                          (item) => ({
                            ...item,
                            companyTravellerAttributes: applyAttributeEditToAttributes(
                              item.companyTravellerAttributes,
                              patch
                            ),
                          })
                        );
                        patchCompanyTravellerListCaches(
                          queryClient,
                          row.companyTravellerShortId,
                          (traveller) => ({
                            ...traveller,
                            companyTravellerAttributes: applyAttributeEditToAttributes(
                              traveller.companyTravellerAttributes,
                              patch
                            ),
                          })
                        );
                      },
                      isStale: () =>
                        fieldEdit.isStale(
                          readCachedAttribute(
                            queryClient,
                            ['travellers', row.journey],
                            (item: TravellerJourneyListItem) => item.shortId === row.shortId,
                            (item: TravellerJourneyListItem) => item.companyTravellerAttributes,
                            attribute.shortId
                          )
                        ),
                      isSuperseded: fieldEdit.isSuperseded,
                    });
                  } else {
                    appendAttributeValueToFormData({
                      formData,
                      key: `journey_attribute__${attribute.shortId}`,
                      attributeType: attribute.attributeType,
                      value,
                    });

                    const beforeAttribute = row.journeyAttributes.find(
                      (existing) => existing.attributeShortId === attribute.shortId
                    );
                    const fieldEdit = startFieldEdit(
                      `${row.shortId}:${attribute.shortId}`,
                      beforeAttribute
                    );
                    const applyPatch = () =>
                      patchTravellerJourneyListCache(
                        queryClient,
                        row.journey,
                        row.shortId,
                        (item) => ({
                          ...item,
                          journeyAttributes: applyAttributeEditToAttributes(
                            item.journeyAttributes,
                            patch
                          ),
                        })
                      );

                    try {
                      // Cannot use the editJourneyTraveller hook for this, since we don't know
                      // the traveller journey shortId and the journeyId at the root of this hook
                      await editJourneyTraveller(row.shortId, formData);
                    } catch (error) {
                      if (!hasOwnErrorSurface(attribute.attributeType)) {
                        handlePossibleNonFieldError(error, t('travellerNotEdited'));
                      }
                      throw error;
                    }

                    applyPatch();

                    reconcileAfterElasticRefresh({
                      refetch: () =>
                        Promise.all([
                          queryClient.refetchQueries(['travellers', row.journey]),
                          queryClient.invalidateQueries(['traveller', row.shortId]),
                        ]),
                      isStale: () =>
                        fieldEdit.isStale(
                          readCachedAttribute(
                            queryClient,
                            ['travellers', row.journey],
                            (item: TravellerJourneyListItem) => item.shortId === row.shortId,
                            (item: TravellerJourneyListItem) => item.journeyAttributes,
                            attribute.shortId
                          )
                        ),
                      isSuperseded: fieldEdit.isSuperseded,
                      reapply: applyPatch,
                    });
                  }
                }
              : undefined,
        },
        ...getValueColumn({
          attributeId,
          title: attribute.title,
          attributeType: attribute.attributeType,
        }),
      ];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes, usersData]);
};

export const useActionColumns = ({
  actionsData,
  extraStateStore,
  onPersist,
  columnControlStore,
  tableStore,
}: {
  actionsData: JourneyType['actionsData'];
  extraStateStore: ExtraStateStore;
  onPersist(newState: TableView): void;
  columnControlStore: TravellersColumnControlStore;
  tableStore: TableStore;
}) => {
  const showSetCustomNameModal = useSetCustomNameModal();
  const { useAddedActionIds, useCustomNames, actions } = extraStateStore;
  const addedActionIds = useAddedActionIds();
  const customNames = useCustomNames();

  const onCustomNameChange = useCallback(
    (id: string, name?: string) => {
      if (!name) {
        actions.removeCustomName(id);
      } else {
        actions.setCustomName(id, name);
      }
      onPersist(tableStore.actions.getState());
    },
    [actions, onPersist, tableStore.actions]
  );

  const onActionClick = useCallback(
    (id: string) => {
      columnControlStore.actions.focusColumn(id);
    },
    [columnControlStore.actions]
  );

  return useMemo(() => {
    const columns: Array<AvailableColumn<TravellerJourneyListItem>> = [];

    (actionsData ?? []).forEach((action) => {
      if (addedActionIds.includes(makeColumnId(action.shortId, 'date_completed'))) {
        columns.push(
          createDateCompletedColumn({
            action,
            customNames,
            onCustomNameChange,
            showSetCustomNameModal,
            onActionClick,
            onRemove: () =>
              columnControlStore.actions.toggleColumn(
                makeColumnId(action.shortId, 'date_completed'),
                false
              ),
          })
        );
      }

      if (addedActionIds.includes(makeColumnId(action.shortId, 'status'))) {
        columns.push(
          createStatusColumn({
            action,
            customNames,
            onCustomNameChange,
            showSetCustomNameModal,
            onActionClick,
            onRemove: () =>
              columnControlStore.actions.toggleColumn(
                makeColumnId(action.shortId, 'status'),
                false
              ),
          })
        );
      }

      Object.entries(action.variables).forEach(([_, { fields: variables }]) => {
        variables.forEach((variable) => {
          const columnId = makeVariableColumnId(action.shortId, variable.id);
          if (addedActionIds.includes(columnId)) {
            columns.push(
              createVariableColumn({
                action,
                variable,
                columnId,
                customNames,
                onCustomNameChange,
                showSetCustomNameModal,
                onActionClick,
                onRemove: () => columnControlStore.actions.toggleColumn(columnId, false),
              })
            );
          }
        });
      });
    });

    return columns;
  }, [
    actionsData,
    addedActionIds,
    customNames,
    onCustomNameChange,
    showSetCustomNameModal,
    onActionClick,
    columnControlStore.actions,
  ]);
};

const createTooltip = (action: ActionsDataAction, label: string, icon: JSX.Element) => {
  return (
    <Fragment>
      <Stack space={1} orientation="horizontal" align="center">
        <ActionIcon action={action.action} size={16} color="white100" />
        <ActionLabel action={action.action} color="white100" />
      </Stack>
      <Stack space={1} orientation="horizontal" align="center">
        <IconSubIndicator size={16} color="white100" />
        {icon}
        <Text color="white100" font="regular12" text={label} />
      </Stack>
    </Fragment>
  );
};

type MenuHeaderParams = {
  action: ActionsDataAction;
  columnId: string;
  defaultName: string;
  customNames: Record<string, string>;
  onCustomNameChange: (id: string, name?: string) => void;
  showSetCustomNameModal: ShowCustomNameModal;
  onActionClick: (id: string) => void;
  icon?: ReactNode;
  closeMenu(): void;
};

const MenuHeaderFieldRow = styled(Stack)({
  '--icon-color': theme.color.elementPurple50,
});

const createMenuHeader = ({
  action,
  columnId,
  defaultName,
  customNames,
  onCustomNameChange,
  showSetCustomNameModal,
  onActionClick,
  closeMenu,
  icon,
}: MenuHeaderParams) => {
  const title = action.name ?? getActionLabel(action.action);
  const actionIcon = action.avatar ? (
    <DesignAvatar src={action.avatar} size={16} />
  ) : action.logo ? (
    <SquareImage src={action.logo} size={16} />
  ) : (
    <ActionIcon action={action.action} size={16} />
  );
  const card = (
    <Card spaceX={2} spaceY={2}>
      <Horizontal space={1} justify="space-between" align="center">
        <div>
          <Stack space={1} orientation="horizontal" align="center">
            {actionIcon}
            <Text font="regular12" text={title} />
          </Stack>
          <MenuHeaderFieldRow space={1} orientation="horizontal" align="center">
            <IconSubIndicator size={16} />
            {icon ?? <IconDateSmall />}
            <Text font="regular12" text={defaultName} />
          </MenuHeaderFieldRow>
        </div>
        <IconButton
          icon={<IconSidemenuRightOpen />}
          variant="ghost-purple"
          size="md"
          onClick={() => {
            onActionClick(columnId);
            closeMenu();
          }}
        />
      </Horizontal>
    </Card>
  );

  return (
    <Stack space={2}>
      {card}
      <Menu.Item
        icon={<IconRename />}
        label={t('customName')}
        onSelect={() => {
          showSetCustomNameModal({
            name: customNames[columnId],
            defaultName,
            card,
            onConfirm: (newName: string) => {
              if (newName === customNames[columnId]) {
                return;
              }
              onCustomNameChange(columnId, newName === defaultName ? undefined : newName);
            },
          });
        }}
      />
    </Stack>
  );
};

type ColumnParams = {
  action: ActionsDataAction;
  customNames: Record<string, string>;
  onCustomNameChange: (id: string, name?: string) => void;
  showSetCustomNameModal: ShowCustomNameModal;
  onActionClick: (id: string) => void;
  onRemove?: () => void;
};

type VariableColumnParams = ColumnParams & {
  variable: ActionsDataField;
  columnId: string;
};

const createDateCompletedColumn = ({
  action,
  customNames,
  onCustomNameChange,
  showSetCustomNameModal,
  onActionClick,
  onRemove,
}: ColumnParams): AvailableColumn<TravellerJourneyListItem> => {
  const id = makeColumnId(action.shortId, 'date_completed');
  const defaultName = t('action_date_completed');

  return {
    id,
    type: 'date',
    title: customNames[id] ?? defaultName,
    renderFallback: () => '-',
    menuHeader: (closeMenu) => {
      return createMenuHeader({
        action,
        columnId: id,
        defaultName,
        customNames,
        onCustomNameChange,
        showSetCustomNameModal,
        onActionClick,
        closeMenu,
      });
    },
    tooltip: () => {
      return createTooltip(
        action,
        t('action_date_completed'),
        <SmallColumnIcon type="date" color="white100" />
      );
    },
    menuFooter: (
      <Menu.Item
        label={t('removeField')}
        variant="danger"
        icon={<IconRemove />}
        onSelect={() => onRemove?.()}
      />
    ),
  };
};

const createStatusColumn = ({
  action,
  customNames,
  onCustomNameChange,
  showSetCustomNameModal,
  onActionClick,
  onRemove,
}: ColumnParams): AvailableColumn<TravellerJourneyListItem> => {
  const id = makeColumnId(action.shortId, 'status');
  const defaultName = t('action_status');

  return {
    id,
    type: 'single_select',
    labelMap: actionStatusLabelMap,
    title: customNames[id] ?? defaultName,
    renderFallback: () => '-',
    renderHeaderIcon: () => <IconStatus size={16} />,
    menuHeader: (closeMenu) => {
      return createMenuHeader({
        action,
        columnId: id,
        defaultName,
        customNames,
        onCustomNameChange,
        showSetCustomNameModal,
        onActionClick,
        closeMenu,
        icon: <IconStatus size={16} color="elementPurple50" />,
      });
    },
    tooltip: () => {
      return createTooltip(action, t('action_status'), <IconStatus size="16" color="white100" />);
    },
    Cell: ({ row }) => {
      const status = row[makeColumnId(action.shortId, 'status') as keyof typeof row];
      return (
        <Badge
          variant={statusTagMap[status as keyof typeof statusTagMap]}
          text={actionStatusLabelMap[status as keyof typeof actionStatusLabelMap]}
        />
      );
    },
    menuFooter: (
      <Menu.Item
        label={t('removeField')}
        variant="danger"
        icon={<IconRemove />}
        onSelect={() => onRemove?.()}
      />
    ),
  };
};

const createVariableColumn = ({
  action,
  variable,
  columnId,
  customNames,
  onCustomNameChange,
  showSetCustomNameModal,
  onActionClick,
  onRemove,
}: VariableColumnParams): AvailableColumn<TravellerJourneyListItem> => {
  const defaultName = variable.label;

  return {
    id: columnId,
    type: variable.type,
    title: customNames[columnId] ?? defaultName,
    renderFallback: () => '-',
    renderHeaderIcon: () => <ColumnIcon type={variable.type} size={16} />,
    tooltip: () => {
      return createTooltip(
        action,
        variable.label,
        <SmallColumnIcon type={variable.type} color="white100" />
      );
    },
    menuHeader: (closeMenu) => {
      return createMenuHeader({
        action,
        columnId,
        defaultName,
        customNames,
        onCustomNameChange,
        showSetCustomNameModal,
        onActionClick,
        closeMenu,
        icon: <ColumnIcon type={variable.type} size={16} />,
      });
    },
    menuFooter: (
      <Menu.Item
        label={t('removeField')}
        variant="danger"
        icon={<IconRemove />}
        onSelect={() => onRemove?.()}
      />
    ),
  };
};

export const makeColumnId = (actionId: string, ...rest: string[]) => {
  const base = `action__${actionId}`;
  if (rest.length === 0) {
    return base;
  }
  return `${base}__${rest.join('__')}`;
};

export const makeVariableColumnId = (actionId: string, ...rest: string[]) => {
  return makeColumnId(actionId, 'vars', ...rest);
};

export const parseActionIdFromColumnId = (columnId: string): string | null => {
  const [prefix, actionId] = columnId.split('__');
  if (prefix !== 'action' || !actionId) {
    return null;
  }
  return actionId;
};

export const makeAttributeId = (
  shortId: AttributeTemplate['shortId'],
  isTravellerAttribute: boolean
) => {
  return isTravellerAttribute ? `attribute-${shortId}` : `journey-attribute-${shortId}`;
};

export const useDebouncedFieldsFetch = (journeyId: string) => {
  const fetchFieldsMutation = useFetchTravellerFields(journeyId);
  const queryClient = useQueryClient();
  const pendingFieldsRef = useRef<Set<string>>(new Set());

  const debouncedFetch = useMemo(
    () =>
      debounce(() => {
        const fieldsToFetch = Array.from(pendingFieldsRef.current);
        pendingFieldsRef.current.clear();

        if (fieldsToFetch.length === 0) return;

        fetchFieldsMutation.mutate(fieldsToFetch, {
          onSuccess: (data) => {
            queryClient.setQueryData<TravellerJourneyListItem[]>(
              ['travellers', journeyId],
              (oldData) => {
                if (!oldData) return oldData;

                const fieldsMap = new Map<string, Record<string, any>>();
                data.forEach(({ travellerId, fields }) => {
                  fieldsMap.set(travellerId, fields);
                });

                return oldData.map((traveller) => {
                  const newFields = fieldsMap.get(traveller.shortId);
                  if (!newFields) return traveller;

                  return {
                    ...traveller,
                    actionsData: {
                      ...traveller.actionsData,
                      ...newFields,
                    },
                  };
                });
              }
            );
          },
          onError: () => {
            showError(t('columnLoadError'));
          },
        });
      }, 1000),
    [fetchFieldsMutation, queryClient, journeyId]
  );

  const handleFieldsNeeded = useEvent((newFieldIds: string[]) => {
    newFieldIds.forEach((id) => pendingFieldsRef.current.add(id));
    debouncedFetch();
  });

  return handleFieldsNeeded;
};
